import React, { useState, useEffect, useMemo } from 'react';
import styled from 'styled-components';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import StudentCard from '../StudentCard';
import QuestionGrading from '../QuestionGrading';
import TeacherSettingsModal from './TeacherSettingsModal';
import { getEnterKeyShortcutPreference } from '../../utils/teacherSettings';
import { Teacher, Student, Question, YoklaAssignment, ScannedData } from '../../types';
import { updateAssignmentStatus, getQuestionPath, detectBlokMode } from '../../services/yoklaService';
import { QRUP_CONFIGS } from '../../constants/fennConfig';
import { doc, updateDoc, Timestamp } from 'firebase/firestore';
import { db } from '../../firebase';
import { useToast } from '../../contexts/ToastContext';
import { useKeyboardShortcuts } from '../../hooks/useKeyboardShortcuts';
import KeyboardShortcutsModal from './KeyboardShortcutsModal';
import { Z_INDEX } from '../../styles/tokens';

interface LocationState {
  assignment: YoklaAssignment;
  students: ScannedData[];
}

interface YoxlamaPageProps {
  teacher: Teacher;
}

const AppContainer = styled.div`
  min-height: 100vh;
  padding: 20px;
  background: #1e3a8a;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const Header = styled.div`
  text-align: center;
  margin-bottom: 40px;
  color: white;
`;

const Title = styled.h1`
  font-size: 36px;
  font-weight: 700;
  margin-bottom: 8px;
  text-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    font-size: 28px;
  }
`;

const Subtitle = styled.p`
  font-size: 18px;
  opacity: 0.9;

  @media (max-width: 768px) {
    font-size: 16px;
  }
`;

const MainContent = styled.div`
  max-width: 1400px;
  margin: 0 auto;
`;

const TeacherInfo = styled.div`
  background: white;
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;

  @media (max-width: 768px) {
    padding: 16px;
  }
`;

const TeacherDetails = styled.div`
  display: flex;
  gap: 24px;
  color: #64748b;
  font-size: 16px;
  align-items: center;
  flex-wrap: wrap;

  @media (max-width: 768px) {
    gap: 12px;
    font-size: 14px;
  }
`;

const BackButton = styled.button`
  background: #3b82f6;
  color: white;
  border: none;
  padding: 8px 16px;
  border-radius: 6px;
  cursor: pointer;
  font-weight: 600;
  transition: all 0.3s ease;

  &:hover {
    background: #2563eb;
    transform: translateY(-1px);
    box-shadow: 0 2px 8px rgba(59, 130, 246, 0.3);
  }

  @media (max-width: 768px) {
    padding: 6px 12px;
    font-size: 13px;
  }
`;

const SettingsButton = styled.button`
  background: #f1f5f9;
  color: #64748b;
  border: 1px solid #e2e8f0;
  padding: 8px 12px;
  border-radius: 6px;
  cursor: pointer;
  font-size: 18px;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  justify-content: center;

  &:hover {
    background: #e2e8f0;
    color: #1e293b;
    transform: scale(1.05);
  }

  @media (max-width: 768px) {
    padding: 6px 10px;
    font-size: 16px;
  }
`;

const MainLayout = styled.div`
  display: grid;
  grid-template-columns: 2fr 1fr;
  gap: 0;
  margin-bottom: 24px;
  border: 1px solid #e2e8f0;
  border-radius: 12px;
  overflow: hidden;
  min-height: 600px;
  position: relative;
  background: white;

  @media (max-width: 1024px) {
    grid-template-columns: 1fr;
    position: relative;
  }
`;

const ZoomControlsArea = styled.div`
  position: absolute;
  top: 20px;
  left: 20px;
  z-index: 100;
  display: flex;
  gap: 8px;
  align-items: center;

  @media (max-width: 1024px) {
    position: relative;
    top: 0;
    left: 0;
    margin-bottom: 16px;
  }
`;

const ZoomButton = styled.button`
  background: rgba(255, 255, 255, 0.9);
  border: 1px solid #e2e8f0;
  border-radius: 6px;
  padding: 8px 12px;
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: all 0.2s ease;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);

  &:hover {
    background: white;
    border-color: #3b82f6;
  }

  &:active {
    transform: scale(0.95);
  }
`;

const QuestionNumberLabel = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
  margin-right: auto;

  @media (min-width: 1025px) {
    display: none;
  }
`;

const ImageSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 16px 18px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);

  @media (max-width: 768px) {
    padding: 12px 14px;
  }
`;

const LeftSection = styled.div`
  background: #f8fafc;
  padding: 20px;
  display: flex;
  flex-direction: column;
  border-right: 1px solid #e2e8f0;
  min-height: 600px;

  @media (max-width: 1024px) {
    min-height: auto;
    border-right: none;
    border-bottom: 1px solid #e2e8f0;
    padding: 16px;
  }
`;

const RightSection = styled.div`
  background: white;
  padding: 20px;
  display: flex;
  flex-direction: column;
  gap: 20px;
  overflow-y: auto;
  max-height: 600px;

  @media (max-width: 1024px) {
    max-height: none;
    padding: 16px;
  }
`;

const StudentsList = styled.div`
  background: #f8fafc;
  border-radius: 8px;
  padding: 12px;
  border: 1px solid #e2e8f0;

  @media (max-width: 1024px) {
    display: none;
  }
`;

const MobileDrawerOverlay = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'block' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: ${Z_INDEX.drawer};
  background: rgba(0, 0, 0, 0.5);
  animation: fadeIn 0.2s ease;

  @media (min-width: 1024px) {
    display: none;
  }

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const MobileDrawerContent = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  max-height: 70vh;
  background: white;
  border-radius: 20px 20px 0 0;
  padding: 20px;
  overflow-y: auto;
  animation: slideUp 0.3s ease;
  box-shadow: 0 -4px 20px rgba(0, 0, 0, 0.15);

  @keyframes slideUp {
    from { transform: translateY(100%); }
    to { transform: translateY(0); }
  }
`;

const DrawerHandle = styled.div`
  width: 40px;
  height: 4px;
  background: #e2e8f0;
  border-radius: 2px;
  margin: 0 auto 16px;
  cursor: pointer;
`;

const MobileStudentNav = styled.div`
  display: none;
  
  @media (max-width: 1024px) {
    display: flex;
    gap: 8px;
    margin-top: 16px;
    padding: 12px;
    background: #f8fafc;
    border-radius: 8px;
    border: 1px solid #e2e8f0;
    align-items: center;
  }
`;

const StudentsTitle = styled.h3`
  font-size: 14px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 8px;
`;

const StudentsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const GradingSection = styled.div`
  background: white;
  border-radius: 12px;
  padding: 24px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  @media (max-width: 1024px) {
    padding: 16px;
    margin-top: 40px;
  }
`;

const SectionTitle = styled.h3`
  font-size: 20px;
  font-weight: 600;
  color: #1e293b;
  margin-bottom: 20px;
`;

const QuestionsSection = styled.div`
  margin-bottom: 24px;
`;

const QuestionNavigation = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 20px;
  padding: 16px;
  background: #f8fafc;
  border-radius: 8px;
  border: 1px solid #e2e8f0;
`;

const NavButton = styled.button`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  border: 1px solid #d1d5db;
  border-radius: 8px;
  background: white;
  color: #374151;
  font-size: 18px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #3b82f6;
    color: white;
    border-color: #3b82f6;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const QuestionInfo = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 4px;
`;

const QuestionCounter = styled.span`
  font-size: 16px;
  font-weight: 600;
  color: #1e293b;
`;

const QuestionStatus = styled.span<{ hasScore: boolean }>`
  font-size: 12px;
  color: ${props => props.hasScore ? '#059669' : '#6b7280'};
  font-weight: 500;
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 12px;
  justify-content: flex-end;
`;

const Button = styled.button<{ variant?: 'primary' | 'secondary' }>`
  padding: 12px 24px;
  border-radius: 8px;
  font-size: 16px;
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  border: none;

  ${props => props.variant === 'primary' ? `
    background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
    color: white;
    
    &:hover {
      transform: translateY(-1px);
      box-shadow: 0 4px 12px rgba(59, 130, 246, 0.3);
    }
  ` : `
    background: #f8fafc;
    color: #64748b;
    border: 1px solid #e2e8f0;
    
    &:hover {
      background: #f1f5f9;
      border-color: #cbd5e1;
    }
  `}

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const LoadingContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 40px;
  color: #64748b;
`;

const ProgressBarWrapper = styled.div<{ $isCompleted?: boolean }>`
  background: ${props => props.$isCompleted
    ? 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)'
    : 'white'};
  border-radius: 12px;
  padding: 14px 18px;
  margin-bottom: 24px;
  box-shadow: 0 2px 10px rgba(15, 23, 42, 0.06);
  border-left: ${props => props.$isCompleted ? '4px solid #10b981' : '4px solid #3b82f6'};
  transition: all 0.3s ease;
`;

const ProgressText = styled.div<{ $isCompleted?: boolean }>`
  font-size: 15px;
  font-weight: 600;
  color: ${props => props.$isCompleted ? '#166534' : '#1e293b'};
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  gap: 8px;

  ${props => props.$isCompleted && `
    &::before {
      content: '✓';
      color: #10b981;
      font-size: 18px;
      font-weight: 700;
    }
  `}
`;

const ProgressBarOuter = styled.div`
  width: 100%;
  height: 10px;
  background: #e2e8f0;
  border-radius: 5px;
  overflow: hidden;
`;

const ProgressBarInner = styled.div<{ $width: number }>`
  height: 100%;
  background: linear-gradient(90deg, #10b981, #3b82f6);
  border-radius: 5px;
  width: ${props => props.$width}%;
  transition: width 0.3s ease;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #e2e8f0;
  border-top: 4px solid #3b82f6;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 16px;

  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
`;

const LoadingText = styled.div`
  font-size: 16px;
  font-weight: 500;
  color: #64748b;
`;

const QuestionImage = styled.div`
  margin-bottom: 24px;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);

  img {
    width: 100%;
    height: auto;
    display: block;
  }
`;

const ImageModal = styled.div<{ $isOpen: boolean }>`
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.95);
  z-index: ${Z_INDEX.modal};
  justify-content: center;
  align-items: center;
  cursor: pointer;
`;

const ModalImageContainer = styled.div`
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ModalImage = styled.img`
  width: 100%;
  height: 100%;
  object-fit: contain;

  @media (max-width: 768px) {
    transform: rotate(90deg);
    width: 100vh;
    height: 100vw;
  }
`;

const CloseButton = styled.button`
  position: fixed;
  top: 20px;
  right: 20px;
  background: white;
  border: none;
  width: 40px;
  height: 40px;
  border-radius: 50%;
  cursor: pointer;
  font-size: 24px;
  font-weight: bold;
  color: #1e293b;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: all 0.2s ease;
  z-index: ${Z_INDEX.modalClose};

  &:hover {
    background: #f1f5f9;
    transform: scale(1.1);
  }
`;

const YoxlamaPage: React.FC<YoxlamaPageProps> = ({ teacher }) => {
  const navigate = useNavigate();
  const { yoxlamaId, questionNumber } = useParams<{ yoxlamaId: string; questionNumber?: string }>();
  const location = useLocation();
  const state = location.state as LocationState;
  const toast = useToast();

  const [students, setStudents] = useState<ScannedData[]>(state?.students || []);
  const [currentAssignment, setCurrentAssignment] = useState<YoklaAssignment | null>(state?.assignment || null);

  // Blok exam üçün: admin "Boş sual" və digər müəllimlərin balladığı şagirdləri lokalda gizlət
  const filteredStudents = useMemo(() => {
    if (!currentAssignment) return students;

    const qField = `${currentAssignment.questionNumber}_c`;

    const filtered = students.filter(student => {
      const qValue: any = (student as any)[qField];

      if (!qValue) {
        // Heç kim qiymətləndirməyib – bu müəllim üçün açıqdır
        return true;
      }

      if (qValue && typeof qValue === 'object') {
        const isAdminEmpty =
          qValue.hocaId === 'admin' &&
          qValue.note &&
          qValue.note.includes('Boş sual - Admin tərəfindən təsdiqləndi');

        if (isAdminEmpty) {
          // Admin boş kağıt – bu müəllim heç görməməlidir
          return false;
        }

        // Başqa müəllim ballayıbsa da, bu müəllimin siyahısına düşməməlidir
        if (qValue.hocaId && qValue.hocaId !== teacher.id) {
          return false;
        }
      }

      return true;
    });

    // Sıralama: Önce normalize edilmiş xdil'e göre (İngilis dili önce, İngilis sonra), sonra document ID'ye göre
    const normalizeXdil = (xdil: string | null | undefined): string => {
      if (!xdil) return '';
      const normalized = xdil.trim();
      if (normalized === 'İngilis' || normalized === 'ingilis' || normalized === 'İNGİLİS') return 'İngilis dili';
      if (normalized === 'Fransız' || normalized === 'fransız' || normalized === 'FRANSIZ') return 'Fransız dili';
      if (normalized === 'Alman' || normalized === 'alman' || normalized === 'ALMAN') return 'Alman dili';
      if (normalized === 'Rus' || normalized === 'rus' || normalized === 'RUS') return 'Rus dili';
      return normalized;
    };

    return filtered.sort((a, b) => {
      // Önce normalize edilmiş xdil'e göre sırala
      const xdilA = normalizeXdil(a.xdil);
      const xdilB = normalizeXdil(b.xdil);

      // Aynı normalize edilmiş xdil ise, orijinal xdil'e göre sırala (İngilis dili önce, İngilis sonra)
      if (xdilA === xdilB) {
        const origXdilA = (a.xdil || '').trim();
        const origXdilB = (b.xdil || '').trim();

        // Eğer biri "İngilis dili" diğeri "İngilis" ise, "İngilis dili" önce gelsin
        if (origXdilA === 'İngilis dili' && origXdilB === 'İngilis') return -1;
        if (origXdilA === 'İngilis' && origXdilB === 'İngilis dili') return 1;
        if (origXdilA === 'Fransız dili' && origXdilB === 'Fransız') return -1;
        if (origXdilA === 'Fransız' && origXdilB === 'Fransız dili') return 1;
        if (origXdilA === 'Alman dili' && origXdilB === 'Alman') return -1;
        if (origXdilA === 'Alman' && origXdilB === 'Alman dili') return 1;
        if (origXdilA === 'Rus dili' && origXdilB === 'Rus') return -1;
        if (origXdilA === 'Rus' && origXdilB === 'Rus dili') return 1;

        // Aynı orijinal xdil ise, document ID'ye göre sırala
        const idA = a.id || a.student_id || '';
        const idB = b.id || b.student_id || '';
        return idA.localeCompare(idB);
      }

      // Farklı xdil ise, normalize edilmiş xdil'e göre sırala
      return xdilA.localeCompare(xdilB);
    });
  }, [students, currentAssignment, teacher.id]);

  const [currentStudentIndex, setCurrentStudentIndex] = useState(0);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [isGradingComplete, setIsGradingComplete] = useState(false);
  // Removed currentQuestionIndex - no longer needed for single-question grading
  const [zoomLevel, setZoomLevel] = useState(1);
  const [isZoomed, setIsZoomed] = useState(false);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [completedStudents, setCompletedStudents] = useState<Set<string>>(new Set());
  const [isLoadingQuestions, setIsLoadingQuestions] = useState(true); // Başlangıçta true olmalı
  const [isReadOnly, setIsReadOnly] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [initialScores, setInitialScores] = useState<{ [key: number]: string | null }>({});
  const [showQuestionsSection, setShowQuestionsSection] = useState(true);
  const [isInitializing, setIsInitializing] = useState(true); // İlk yükleme için ayrı state
  const [enterKeyEnabled, setEnterKeyEnabled] = useState(true);
  const [showSettingsModal, setShowSettingsModal] = useState(false);
  const [isStudentLoading, setIsStudentLoading] = useState(false);
  const [showShortcutsModal, setShowShortcutsModal] = useState(false);
  const [isMobileDrawerOpen, setIsMobileDrawerOpen] = useState(false);

  const currentStudent = filteredStudents[currentStudentIndex];

  // İsim sansürleme fonksiyonu
  const censorName = (name: string): string => {
    if (!name) return '';
    const firstChar = name.charAt(0);
    const stars = '*'.repeat(Math.max(name.length - 1, 1));
    return firstChar + stars;
  };

  // Soyisim tamamen yıldız
  const censorLastName = (name: string): string => {
    if (!name) return '';
    return '*'.repeat(name.length);
  };

  useEffect(() => {
    if (!state || !state.assignment || !state.students) {
      navigate('/dashboard');
      return;
    }

    // Atama statusuna göre read-only modunu ayarla
    const isReadOnlyMode = currentAssignment?.status === 'completed' || currentAssignment?.status === 'passive';
    setIsReadOnly(isReadOnlyMode);

    // Tamamlanan öğrencileri kontrol et ve en son yoklanan öğrenciyi bul
    const loadInitialStudent = async () => {
      if (!currentAssignment) return;

      setIsInitializing(true);
      setIsLoadingQuestions(true);

      // Q##_c formatını birbaşa student obyektindən yoxla (BuraxilisYoxlamaPage.tsx pattern)
      const currentQuestionNum = questionNumber || '';
      const questionField = `${currentQuestionNum}_c`;

      const completedSet = new Set<string>();
      let lastCompletedIndex = -1;

      filteredStudents.forEach((student, index) => {
        const gradingData: any = (student as any)[questionField];
        const isCompleted = gradingData &&
                            typeof gradingData === 'object' &&
                            gradingData.score &&
                            gradingData.hocaId === teacher.id;

        if (isCompleted) {
          completedSet.add(student.student_id);
          lastCompletedIndex = index;
        }
      });

      setCompletedStudents(completedSet);

      // En son yoklanan öğrenciden devam et, yoksa ilk yoklanmamış öğrenciden başla
      const startIndex = lastCompletedIndex >= 0
        ? Math.min(lastCompletedIndex + 1, filteredStudents.length - 1)  // Sonraki yoklanmamış öğrenci
        : 0;

      setCurrentStudentIndex(startIndex);
      await loadStudentQuestions(filteredStudents[startIndex], currentAssignment.fenn);
      setIsInitializing(false);
    };
    
    loadInitialStudent();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // sortedStudents useMemo ile hesaplanıyor, students değişince otomatik güncellenir

  // Redirect for old URL format: if questionNumber is missing, redirect to first question
  useEffect(() => {
    if (!questionNumber && currentAssignment && currentAssignment.questionNumbers && currentAssignment.questionNumbers.length > 0) {
      // Old URL format - redirect to first question
      const firstQuestion = currentAssignment.questionNumbers[0];
      console.log('⚠️ YoxlamaPage: Köhnə URL formatı - ilk suala yönləndirilir:', firstQuestion);
      navigate(`/dashboard/yoxlama/${yoxlamaId}/${firstQuestion}`, {
        state: { assignment: currentAssignment, students },
        replace: true
      });
    } else if (!questionNumber && currentAssignment) {
      // Assignment without questionNumbers (legacy format) - determine first question
      const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === currentAssignment.qrup);
      const fennRange = qrupConfig?.fennler.find(f => f.fenn === currentAssignment.fenn);
      if (fennRange) {
        const firstQuestion = `Q${fennRange.end - 2}`;
        console.log('⚠️ YoxlamaPage: Legacy assignment - hesablanan ilk suala yönləndirilir:', firstQuestion);
        navigate(`/dashboard/yoxlama/${yoxlamaId}/${firstQuestion}`, {
          state: { assignment: currentAssignment, students },
          replace: true
        });
      }
    }
  }, [questionNumber, currentAssignment, filteredStudents, yoxlamaId, navigate]);

  // questions veya initialScores değiştiğinde hasChanges'i otomatik hesapla
  useEffect(() => {
    if (completedStudents.has(currentStudent?.student_id) && Object.keys(initialScores).length > 0) {
      const hasAnyChange = questions.some(q =>
        initialScores[q.questionNumber] !== q.score
      );
      setHasChanges(hasAnyChange);
    }
  }, [questions, initialScores, currentStudent, completedStudents]);

  // Enter key ayarını yükle - mount edildiğinde
  useEffect(() => {
    setEnterKeyEnabled(getEnterKeyShortcutPreference());
  }, []);

  // imageUrl yoksa isStudentLoading'i sıfırla
  useEffect(() => {
    if (!currentAssignment || !currentStudent) return;
    const imgInfo = getImageInfo(currentAssignment.fenn);
    if (!imgInfo.imageUrl) {
      setIsStudentLoading(false);
      setImageLoading(false);
    }
  }, [currentStudentIndex, currentAssignment]);

  // Keyboard shortcuts
  useKeyboardShortcuts({
    shortcuts: [
      {
        key: 'ArrowLeft',
        description: 'Əvvəlki şagird',
        action: () => handlePreviousStudent(),
        disabled: currentStudentIndex === 0 || isSaving
      },
      {
        key: 'ArrowRight',
        description: 'Növbəti şagird',
        action: () => handleNextStudent(),
        disabled: currentStudentIndex === filteredStudents.length - 1 || isSaving
      },
      {
        key: 'Escape',
        description: 'Modalı bağla',
        action: () => {
          if (isImageModalOpen) setIsImageModalOpen(false);
          if (showShortcutsModal) setShowShortcutsModal(false);
          if (showSettingsModal) setShowSettingsModal(false);
        }
      },
      {
        key: '?',
        description: 'Qısa yolları göstər',
        action: () => setShowShortcutsModal(true)
      },
      {
        key: 'Enter',
        description: 'Yadda saxla',
        action: () => {
          if (!enterKeyEnabled) return;
          const confirmButton = document.querySelector('[data-confirm-button]') as HTMLButtonElement;
          if (confirmButton && !confirmButton.disabled) {
            confirmButton.click();
          }
        },
        disabled: isSaving
      },
    ],
    enabled: !isInitializing && !isReadOnly
  });

  const getQuestionNumbers = (subject: string): number[] => {
    if (!currentAssignment) {
      return [28, 29, 30]; // Varsayılan
    }
    
    // Qrup ve Fenn'e göre soru aralığını bul
    const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === currentAssignment.qrup);
    const fennRange = qrupConfig?.fennler.find(f => f.fenn === subject);
    
    if (!fennRange) {
      console.warn('Fenn aralığı bulunamadı:', subject, currentAssignment.qrup);
      return [28, 29, 30]; // Varsayılan
    }
    
    // Sual aralığına görə son 3 sualı qaytarır
    // 1-30 arası → [28, 29, 30]
    // 31-60 arası → [58, 59, 60]
    // 61-90 arası → [88, 89, 90]
    
    if (fennRange.start >= 1 && fennRange.end <= 30) {
      return [28, 29, 30];
    } else if (fennRange.start >= 31 && fennRange.end <= 60) {
      return [58, 59, 60];
    } else if (fennRange.start >= 61 && fennRange.end <= 90) {
      return [88, 89, 90];
    }
    
    // Varsayılan olarak aralığın son 3 sorusu
    return [fennRange.end - 2, fennRange.end - 1, fennRange.end];
  };

  const loadStudentQuestions = async (student: ScannedData, fenn: string) => {
    try {
      console.log('📚 YoxlamaPage: Şagird sualları yüklənir:', student.student_id, fenn);

      setIsLoadingQuestions(true);

      // Use questionNumber from URL instead of getQuestionNumbers()
      const currentQuestionNum = questionNumber || '';
      const questionNumAsNumber = parseInt(currentQuestionNum.replace('Q', ''), 10);

      // Load single question instead of multiple questions
      const emptyQuestions = [{
        questionNumber: questionNumAsNumber,
        score: null as string | null,
        note: ''
      }];
      setQuestions(emptyQuestions);

      setTimeout(() => {
        setQuestions(prev => [...prev]);
      }, 0);

      // Q##_c formatını birbaşa student obyektindən oxu (BuraxilisYoxlamaPage.tsx pattern)
      const gradingFieldName = `${currentQuestionNum}_c`;
      const gradingData: any = (student as any)[gradingFieldName];

      if (gradingData && typeof gradingData === 'object' && gradingData.score) {
        const score = gradingData.score;
        const note = gradingData.note || '';

        const questionsWithData = [{
          questionNumber: questionNumAsNumber,
          score,
          note
        }];

        console.log('📊 YoxlamaPage: Mövcud qiymətləndirmə yükləndi:', questionsWithData);

        // İlk skorları kaydet (değişiklik kontrolü için)
        const initialScoresMap: { [key: number]: string | null } = {};
        questionsWithData.forEach(q => {
          initialScoresMap[q.questionNumber] = q.score;
        });
        setInitialScores(initialScoresMap);

        // Sual dolu mu kontrol et
        const allCompleted = questionsWithData.every(q => q.score !== null);
        setIsGradingComplete(allCompleted);
        setShowQuestionsSection(!allCompleted); // Tamamlandıysa soruları gizle
        setHasChanges(false);

        setTimeout(() => {
          setQuestions(questionsWithData);
          setTimeout(() => {
            setQuestions(prev => [...prev]);
            setIsLoadingQuestions(false);
          }, 10);
        }, 100);
      } else {
        console.log('📊 YoxlamaPage: Mövcud qiymətləndirmə yoxdur, boş suallar istifadə olunur');
        setInitialScores({});
        setIsGradingComplete(false);
        setShowQuestionsSection(true);
        setHasChanges(false);
        setIsLoadingQuestions(false);
      }
    } catch (error) {
      console.error('❌ YoxlamaPage: Şagird sualları yüklənərkən xəta:', error);
      // Use questionNumber from URL instead of getQuestionNumbers()
      const currentQuestionNum = questionNumber || '';
      const questionNumAsNumber = parseInt(currentQuestionNum.replace('Q', ''), 10);
      const initialQuestions = [{
        questionNumber: questionNumAsNumber,
        score: null as string | null,
        note: ''
      }];
      setQuestions(initialQuestions);
      setIsLoadingQuestions(false);
    }
  };

  const getImageInfo = (subject: string): { imageUrl: string; section: 'left' | 'right' } => {
    const student = filteredStudents[currentStudentIndex];

    if (!student || !currentAssignment) {
      return { imageUrl: '', section: 'left' };
    }

    // First, try to get the specific question path using the utility (supports both legacy and new formats)
    const currentQuestionNum = questionNumber || '';
    if (currentQuestionNum) {
      const specificQuestionPath = getQuestionPath(student, currentQuestionNum);
      if (specificQuestionPath) {
        console.log(`✅ YoxlamaPage: Using specific question path for ${currentQuestionNum}:`, specificQuestionPath);
        return { imageUrl: specificQuestionPath, section: 'left' };
      }
    }

    // Fallback to full page images based on question range (legacy behavior)
    // Qrup ve Fenn'e göre soru aralığını bul
    const qrupConfig = QRUP_CONFIGS.find(q => q.qrup === currentAssignment.qrup);
    const fennRange = qrupConfig?.fennler.find(f => f.fenn === subject);

    if (!fennRange) {
      console.warn('Fenn aralığı bulunamadı:', subject, currentAssignment.qrup);
      return { imageUrl: student.arka_uz_sol || '', section: 'left' };
    }

    // Sual nömrəsinə görə düzgün şəkil sahəsini seç
    // Resimlerdeki soru konumları:
    // arka_uz_sol: 28-30 (1-30 arası sorular)
    // arka_uz_sag: 58-60 (31-60 arası sorular)
    // on_uz_sol: 88-90 (61-90 arası sorular)

    if (fennRange.start >= 1 && fennRange.end <= 30) {
      // 1-30 arası sorular → arka_uz_sol
      return {
        imageUrl: student.arka_uz_sol || '',
        section: 'left'
      };
    } else if (fennRange.start >= 31 && fennRange.end <= 60) {
      // 31-60 arası sorular → arka_uz_sag
      return {
        imageUrl: student.arka_uz_sag || '',
        section: 'right'
      };
    } else if (fennRange.start >= 61 && fennRange.end <= 90) {
      // 61-90 arası sorular → on_uz_sol
      return {
        imageUrl: student.on_uz_sol || '',
        section: 'left'
      };
    }

    // Varsayılan
    return {
      imageUrl: student.arka_uz_sol || '',
      section: 'left'
    };
  };

  const handleScoreChange = (questionNumber: number, score: string | null, note?: string) => {
    setQuestions(prev => {
      const updatedQuestions = prev.map(q => 
        q.questionNumber === questionNumber 
          ? { ...q, score: score, note: note || '' }
          : q
      );
      
      const allCompleted = updatedQuestions.every(q => q.score !== null);
      
      // İlk değerlendirmede tüm sorular tamamsa isGradingComplete = true
      if (allCompleted && !completedStudents.has(currentStudent?.student_id)) {
        setTimeout(() => setIsGradingComplete(true), 0);
      }
      
      // hasChanges useEffect'te otomatik hesaplanacak
      
      return updatedQuestions;
    });
  };

  // Question navigation functions removed - single question grading only

  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + 1, 5));
    setIsZoomed(true);
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => {
      const newLevel = Math.max(prev - 1, 1);
      if (newLevel <= 1) {
        setIsZoomed(false);
        return 1;
      }
      return newLevel;
    });
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
    setIsZoomed(false);
  };

  const handleCompleteGrading = async () => {
    if (!currentStudent || !teacher || !currentAssignment) return;
    if (isSaving) return; // Çift tıklama önleme

    // Düzeliş modundaysa ve gerçekten bir değişiklik yoksa, sadece kapat
    if (isGradingComplete && completedStudents.has(currentStudent.student_id)) {
      const hasRealChanges = questions.some(q => {
        const initialScore = initialScores[q.questionNumber];
        const currentScore = q.score;
        return initialScore !== currentScore;
      });

      if (!hasRealChanges) {
        // Gerçek bir değişiklik yoksa, sadece soruları kapat
        setShowQuestionsSection(false);
        setHasChanges(false);
        return;
      }
    }

    try {
      setIsSaving(true);

      // Q##_c formatında birbaşa Firestore-a yaz (BuraxilisYoxlamaPage.tsx pattern)
      const currentQuestionNum = questionNumber || '';
      const gradingFieldName = `${currentQuestionNum}_c`;
      const studentDocRef = doc(db, currentAssignment.collectionName!, currentStudent.id!);

      // İlk ve tek question-dan score al (single question grading)
      const score = questions[0]?.score || null;
      const note = questions[0]?.note || '';

      if (!score) {
        toast.warning('Bal seçin', 'Zəhmət olmasa bal seçin');
        setIsSaving(false);
        return;
      }

      const gradingData = {
        score: score,
        hocaId: teacher.id,
        hoca: teacher.name,
        timestamp: Timestamp.now(),
        ...(note.trim() && { note: note.trim() })
      };

      await updateDoc(studentDocRef, {
        [gradingFieldName]: gradingData
      });

      console.log('💾 YoxlamaPage: Q##_c formatında kaydedildi:', {
        studentId: currentStudent.student_id,
        field: gradingFieldName,
        gradingData
      });

      // Local state-ə də yaz ki, yenidən açanda Q##_c dəyəri olsun
      setStudents(prev =>
        prev.map(s =>
          s.id === currentStudent.id
            ? ({ ...s, [gradingFieldName]: gradingData } as ScannedData)
            : s
        )
      );

      toast.success('Təsdiqləndi', 'Dəyərləndirmə uğurla yadda saxlanıldı');

      setCompletedStudents(prev => new Set([...prev, currentStudent.student_id]));
      setIsGradingComplete(true);
      setShowQuestionsSection(false); // Sualları gizlə
      setHasChanges(false);

      // İlk skorları güncelle
      const newInitialScores: { [key: number]: string | null } = {};
      questions.forEach(q => {
        newInitialScores[q.questionNumber] = q.score;
      });
      setInitialScores(newInitialScores);

      // Auto-advance to next student after successful save
      // Show message first, then advance after a short delay
      setTimeout(() => {
        if (currentStudentIndex < filteredStudents.length - 1) {
          handleNextStudent();
        }
      }, 500);

    } catch (error) {
      console.error('❌ YoxlamaPage: Qiymətləndirmə yadda saxlanarkən xəta:', error);
      toast.error('Xəta baş verdi', 'Qiymət yadda saxlanılarkən xəta baş verdi. İnternet bağlantısını yoxlayın.');
    } finally {
      setIsSaving(false);
    }
  };
  
  const handleEditGrading = () => {
    setShowQuestionsSection(true);
    // isGradingComplete'i false yapma, çünkü bu düzəliş modu!
  };

  const handleNextStudent = () => {
    if (currentStudentIndex < filteredStudents.length - 1) {
      const nextIndex = currentStudentIndex + 1;

      // Zoom'u reset et
      handleResetZoom();

      setIsStudentLoading(true);
      setImageLoading(true);
      setIsLoadingQuestions(true);
      setCurrentStudentIndex(nextIndex);

      // Sonraki öğrenci tamamlanmamışsa isGradingComplete'i false yap
      if (!completedStudents.has(filteredStudents[nextIndex]?.student_id)) {
        setIsGradingComplete(false);
      }

      setShowQuestionsSection(true);
      setHasChanges(false);

      // Use questionNumber from URL instead of getQuestionNumbers()
      const currentQuestionNum = questionNumber || '';
      const questionNumAsNumber = parseInt(currentQuestionNum.replace('Q', ''), 10);
      const emptyQuestions = [{
        questionNumber: questionNumAsNumber,
        score: null as string | null,
        note: ''
      }];

      setQuestions([]);
      setTimeout(() => {
        setQuestions(emptyQuestions);
      }, 0);

      setTimeout(async () => {
        if (currentAssignment && filteredStudents[nextIndex]) {
          await loadStudentQuestions(filteredStudents[nextIndex], currentAssignment.fenn);
        }
      }, 100);
    } else {
      handleAllStudentsCompleted();
    }
  };

  const handlePreviousStudent = () => {
    if (currentStudentIndex > 0) {
      const prevIndex = currentStudentIndex - 1;

      // Zoom'u reset et
      handleResetZoom();

      setIsStudentLoading(true);
      setImageLoading(true);
      setIsLoadingQuestions(true);
      setCurrentStudentIndex(prevIndex);
      setIsGradingComplete(false);

      // Use questionNumber from URL instead of getQuestionNumbers()
      const currentQuestionNum = questionNumber || '';
      const questionNumAsNumber = parseInt(currentQuestionNum.replace('Q', ''), 10);
      const emptyQuestions = [{
        questionNumber: questionNumAsNumber,
        score: null as string | null,
        note: ''
      }];

      setQuestions([]);
      setTimeout(() => {
        setQuestions(emptyQuestions);
      }, 0);

      setTimeout(async () => {
        if (currentAssignment && filteredStudents[prevIndex]) {
          await loadStudentQuestions(filteredStudents[prevIndex], currentAssignment.fenn);
        }
      }, 100);
    }
  };

  const handleStudentSelect = async (index: number) => {
    console.log('🎯 YoxlamaPage: Öğrenci seçildi:', index, filteredStudents[index]);

    // Zoom'u reset et
    handleResetZoom();

    setIsStudentLoading(true);
    setImageLoading(true);
    setIsLoadingQuestions(true);
    setCurrentStudentIndex(index);

    // Seçilen öğrenci tamamlanmamışsa isGradingComplete'i false yap
    if (!completedStudents.has(filteredStudents[index]?.student_id)) {
      setIsGradingComplete(false);
    }

    setShowQuestionsSection(true);
    setHasChanges(false);

    // Use questionNumber from URL instead of getQuestionNumbers()
    const currentQuestionNum = questionNumber || '';
    const questionNumAsNumber = parseInt(currentQuestionNum.replace('Q', ''), 10);
    const emptyQuestions = [{
      questionNumber: questionNumAsNumber,
      score: null as string | null,
      note: ''
    }];

    setQuestions([]);
    setTimeout(() => {
      setQuestions(emptyQuestions);
    }, 0);

    setTimeout(async () => {
      if (currentAssignment && filteredStudents[index]) {
        await loadStudentQuestions(filteredStudents[index], currentAssignment.fenn);
      }
    }, 200);
  };

  const handleAllStudentsCompleted = async () => {
    console.log('🎉 Tüm öğrenciler tamamlandı!');
    
    try {
      if (currentAssignment) {
        await updateAssignmentStatus(currentAssignment.id, 'completed');
        console.log('✅ Atama statusu tamamlandı olarak güncellendi');
      }
      
      navigate('/dashboard');
    } catch (error) {
      console.error('❌ Atama statusu güncellenirken hata:', error);
    }
  };

  if (!currentAssignment || filteredStudents.length === 0) {
    return (
      <AppContainer>
        <Header>
          <Title>İmtahan Qiymətləndirmə Sistemi</Title>
          <Subtitle>Yüklənir...</Subtitle>
        </Header>
        <MainContent>
          <TeacherInfo>
            <BackButton onClick={() => navigate('/dashboard')}>
              ← Geri Qayıt
            </BackButton>
          </TeacherInfo>
        </MainContent>
      </AppContainer>
    );
  }

  if (isInitializing) {
    return (
      <div style={{ 
        minHeight: '100vh', 
        background: 'white',
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center'
      }}>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '24px',
          padding: '40px'
        }}>
          <div style={{
            width: '80px',
            height: '80px',
            border: '6px solid #e0e7ff',
            borderTop: '6px solid #3b82f6',
            borderRadius: '50%',
            animation: 'spin 1s linear infinite'
          }} />
          <div style={{ 
            fontSize: '20px', 
            color: '#3b82f6', 
            fontWeight: '600',
            textAlign: 'center'
          }}>
            Yoxlama məlumatları yüklənir...
          </div>
          <div style={{
            fontSize: '14px',
            color: '#60a5fa',
            fontWeight: '500'
          }}>
            Zəhmət olmasa gözləyin
          </div>
        </div>
      </div>
    );
  }

  return (
    <AppContainer>
      <Header>
        <Title>İmtahan Qiymətləndirmə Sistemi</Title>
        <Subtitle>Şagird cavab kağızlarını qiymətləndirin</Subtitle>
      </Header>

      <MainContent>
        <TeacherInfo>
          <TeacherDetails>
            <span>Fənn: {teacher.fenn}</span>
            <span>
              Atama: {currentAssignment.fenn} - Sinif {currentAssignment.sinif} - Qrup {currentAssignment.qrup}
              {currentAssignment.subGroup && ` (${currentAssignment.subGroup})`}
              {' '}
              ({completedStudents.size}/{filteredStudents.length} tamamlandı)
            </span>
          </TeacherDetails>
          <div style={{ display: 'flex', gap: '12px' }}>
            <SettingsButton onClick={() => setShowSettingsModal(true)} title="Tənzimləmələr">
              ⚙️
            </SettingsButton>
            <BackButton onClick={() => navigate('/dashboard')}>
              ← Geriyə Qayıt
            </BackButton>
          </div>
        </TeacherInfo>

        <ProgressBarWrapper $isCompleted={completedStudents.size === filteredStudents.length && filteredStudents.length > 0}>
          <ProgressText $isCompleted={completedStudents.size === filteredStudents.length && filteredStudents.length > 0}>
            {completedStudents.size} / {filteredStudents.length} şagird qiymətləndirildi
            {filteredStudents.length > 0 && ` (${Math.round((completedStudents.size / filteredStudents.length) * 100)}%)`}
          </ProgressText>
          <ProgressBarOuter>
            <ProgressBarInner $width={filteredStudents.length > 0 ? (completedStudents.size / filteredStudents.length) * 100 : 0} />
          </ProgressBarOuter>
        </ProgressBarWrapper>

        <MainLayout>
          <LeftSection>
            <ImageSection>
              <ZoomControlsArea>
                <QuestionNumberLabel>
                  {questionNumber ? `Sual ${questionNumber.replace('Q', '')}` : ''}
                </QuestionNumberLabel>
                <ZoomButton onClick={handleZoomIn}>+</ZoomButton>
                <ZoomButton onClick={handleZoomOut}>-</ZoomButton>
                <ZoomButton onClick={handleResetZoom}>Reset</ZoomButton>
                {isZoomed && !imageLoading && (
                  <div style={{
                    fontSize: '13px',
                    fontWeight: '500',
                    color: '#64748b',
                    marginLeft: '12px'
                  }}>
                    Zoom: {Math.round(zoomLevel * 100)}%
                  </div>
                )}
              </ZoomControlsArea>

              <QuestionImage style={{
                position: 'relative',
                overflow: 'auto',
                cursor: isZoomed ? 'grab' : 'zoom-in'
              }}>
                {imageLoading && (
                  <LoadingContainer>
                    <LoadingSpinner />
                    <LoadingText>Şəkil yüklənir...</LoadingText>
                  </LoadingContainer>
                )}
                <img
                  src={getImageInfo(currentAssignment.fenn).imageUrl}
                  alt={`${questionNumber}`}
                  style={{
                    width: isZoomed ? `${100 * zoomLevel}%` : '100%',
                    height: 'auto',
                    objectFit: 'contain',
                    display: imageLoading ? 'none' : 'block',
                    cursor: 'zoom-in'
                  }}
                  onLoad={() => { setImageLoading(false); setIsStudentLoading(false); }}
                  onError={() => { setImageLoading(false); setIsStudentLoading(false); }}
                  onClick={() => setIsImageModalOpen(true)}
                />
              </QuestionImage>
            </ImageSection>
          </LeftSection>

          <RightSection>
            <GradingSection>
              <SectionTitle>
                {currentStudentIndex + 1}. {censorName(currentStudent?.firstName || '')} {censorLastName(currentStudent?.lastName || '')} - {currentAssignment.fenn} Qiymətləndirməsi
              </SectionTitle>

              {isStudentLoading || isLoadingQuestions || questions.length === 0 ? (
                <div style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  minHeight: '400px',
                  background: 'white',
                  borderRadius: '12px',
                  gap: '20px',
                  padding: '40px'
                }}>
                  <div style={{
                    width: '60px',
                    height: '60px',
                    border: '5px solid #e0e7ff',
                    borderTop: '5px solid #3b82f6',
                    borderRadius: '50%',
                    animation: 'spin 1s linear infinite'
                  }} />
                  <div style={{ 
                    fontSize: '18px', 
                    color: '#3b82f6', 
                    fontWeight: '600',
                    textAlign: 'center'
                  }}>
                    Şagird məlumatları yüklənir...
                  </div>
                  <div style={{
                    fontSize: '13px',
                    color: '#60a5fa',
                    fontWeight: '500'
                  }}>
                    Zəhmət olmasa gözləyin
                  </div>
                </div>
              ) : (
                <>
                  {showQuestionsSection && (
                    <QuestionsSection>
                      <QuestionNavigation>
                        <QuestionInfo>
                          <QuestionCounter>
                            Sual {questions[0]?.questionNumber || questionNumber?.replace('Q', '') || 0}
                          </QuestionCounter>
                          <QuestionStatus hasScore={questions[0]?.score !== null}>
                            {questions[0]?.score !== null ? 'Tamamlandı' : 'Bal verilməyib'}
                          </QuestionStatus>
                        </QuestionInfo>
                      </QuestionNavigation>

                      {questions[0] && (
                        <QuestionGrading
                          key={`${questions[0].questionNumber}-${currentStudentIndex}`}
                          questionNumber={questions[0].questionNumber}
                          onScoreChange={handleScoreChange}
                          initialScore={questions[0].score || undefined}
                          initialNote={questions[0].note}
                          readOnly={isReadOnly}
                        />
                      )}
                    </QuestionsSection>
                  )}

                  {!isReadOnly && isGradingComplete && completedStudents.has(currentStudent?.student_id) && (
                    <div style={{ 
                      padding: '12px 24px', 
                      background: '#dcfce7', 
                      borderRadius: '8px', 
                      color: '#166534',
                      textAlign: 'center',
                      border: '2px solid #86efac',
                      marginBottom: '16px',
                      fontWeight: '600'
                    }}>
                      ✅ Dəyərləndirmə təsdiqləndi
                    </div>
                  )}

                  <ActionButtons>
              {!isReadOnly && !completedStudents.has(currentStudent?.student_id) && (
                <>
                  <Button
                    variant="primary"
                    onClick={handleCompleteGrading}
                    disabled={questions.some(q => q.score === null) || isSaving}
                    data-confirm-button="true"
                  >
                    {isSaving ? '⏳ Qeyd edilir...' : `Dəyərləndirməni Təsdiq Et (${questions.filter(q => q.score !== null).length}/${questions.length})`}
                  </Button>
                  {enterKeyEnabled && (
                    <div style={{
                      marginTop: '8px',
                      textAlign: 'center',
                      fontSize: '13px',
                      color: '#64748b',
                      fontWeight: '500'
                    }}>
                      ⏎ Enter tuşu ilə təsdiqlə
                    </div>
                  )}
                </>
              )}

              {!isReadOnly && isGradingComplete && completedStudents.has(currentStudent?.student_id) && (
                <>
                  <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap' }}>
                    {!showQuestionsSection && (
                      <Button
                        variant="secondary"
                        onClick={handleEditGrading}
                        disabled={isSaving}
                        style={{ flex: 1 }}
                      >
                        ✏️ Düzəliş Et
                      </Button>
                    )}

                    {showQuestionsSection && (
                      <>
                        <Button
                          variant="primary"
                          onClick={handleCompleteGrading}
                          disabled={!hasChanges || isSaving}
                          style={{ flex: 1 }}
                          data-confirm-button="true"
                        >
                          {isSaving ? '⏳ Yenilənir...' : '💾 Düzəlişləri Təstiq Et'}
                        </Button>
                        {enterKeyEnabled && (
                          <div style={{
                            marginTop: '8px',
                            textAlign: 'center',
                            fontSize: '13px',
                            color: '#64748b',
                            fontWeight: '500',
                            flex: '100%'
                          }}>
                            ⏎ Enter tuşu ilə təsdiqlə
                          </div>
                        )}
                      </>
                    )}

                    {/* Auto-advance makes this button redundant - commented out */}
                    {/* {currentStudentIndex < students.length - 1 && (
                      <Button
                        variant="primary"
                        onClick={handleNextStudent}
                        disabled={isSaving}
                        style={{ flex: 1 }}
                      >
                        ➡️ Növbəti Yoxlamaya Keçid
                      </Button>
                    )} */}

                    {currentStudentIndex === filteredStudents.length - 1 && (
                      <Button
                        variant="primary"
                        onClick={handleAllStudentsCompleted}
                        disabled={isSaving}
                        style={{ flex: 1 }}
                      >
                        ✅ Bütün Yoxlamalar Tamamlandı
                      </Button>
                    )}
                  </div>
                </>
              )}
              
                    {isReadOnly && (
                      <div style={{ 
                        padding: '12px 24px', 
                        background: '#f8fafc', 
                        borderRadius: '8px', 
                        color: '#64748b',
                        textAlign: 'center',
                        border: '1px solid #e2e8f0'
                      }}>
                        🔒 Sadece görüntüleme modu - Düzenleme yapılamaz
                      </div>
                    )}
                  </ActionButtons>

                  {/* Mobil için öğrenci navigasyonu - EN ALTA */}
                  <MobileStudentNav>
                    <Button
                      variant="secondary"
                      onClick={() => handleStudentSelect(Math.max(currentStudentIndex - 1, 0))}
                      disabled={currentStudentIndex === 0 || isSaving}
                      style={{ flex: 1 }}
                      title="Əvvəlki şagirdə qayıt"
                    >
                      ← Əvvəlki
                    </Button>
                    <div style={{
                      padding: '8px 16px',
                      background: '#f1f5f9',
                      borderRadius: '6px',
                      fontSize: '14px',
                      fontWeight: '600',
                      color: '#475569',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px',
                      cursor: 'pointer'
                    }}
                      onClick={() => setIsMobileDrawerOpen(true)}
                      title="Şagird siyahısını aç"
                    >
                      <span>{currentStudentIndex + 1} / {filteredStudents.length}</span>
                      {completedStudents.has(currentStudent?.student_id) && <span>✅</span>}
                    </div>
                    <Button
                      variant="secondary"
                      onClick={() => handleStudentSelect(Math.min(currentStudentIndex + 1, filteredStudents.length - 1))}
                      disabled={
                        currentStudentIndex === filteredStudents.length - 1 ||
                        isSaving ||
                        questions.some(q => q.score === null)
                      }
                      style={{ flex: 1 }}
                      title={questions.some(q => q.score === null) ? "Növbəti şagirdə keçmək üçün qiymətləndirməni tamamlayın" : "Növbəti şagirdə keç"}
                    >
                      Növbəti →
                    </Button>
                  </MobileStudentNav>
                </>
              )}
            </GradingSection>

            <StudentsList>
              <StudentsTitle>Şagirdlər ({filteredStudents.length})</StudentsTitle>
              <StudentsGrid>
                {filteredStudents.map((student, index) => (
                  <StudentCard
                    key={student.student_id}
                    student={student}
                    isActive={index === currentStudentIndex}
                    isCompleted={completedStudents.has(student.student_id)}
                    onClick={() => handleStudentSelect(index)}
                  />
                ))}
              </StudentsGrid>
            </StudentsList>
          </RightSection>
        </MainLayout>
      </MainContent>

      <TeacherSettingsModal
        isOpen={showSettingsModal}
        onClose={() => setShowSettingsModal(false)}
        onSettingChange={() => setEnterKeyEnabled(getEnterKeyShortcutPreference())}
      />

      <KeyboardShortcutsModal
        isOpen={showShortcutsModal}
        onClose={() => setShowShortcutsModal(false)}
      />

      {/* Mobile Students List Drawer */}
      <MobileDrawerOverlay $isOpen={isMobileDrawerOpen} onClick={() => setIsMobileDrawerOpen(false)}>
        <MobileDrawerContent onClick={(e) => e.stopPropagation()}>
          <DrawerHandle />
          <StudentsTitle>Şagirdlər ({filteredStudents.length})</StudentsTitle>
          <StudentsGrid>
            {filteredStudents.map((student, index) => (
              <StudentCard
                key={student.student_id}
                student={student}
                isActive={index === currentStudentIndex}
                isCompleted={completedStudents.has(student.student_id)}
                onClick={() => {
                  handleStudentSelect(index);
                  setIsMobileDrawerOpen(false);
                }}
              />
            ))}
          </StudentsGrid>
        </MobileDrawerContent>
      </MobileDrawerOverlay>

      {/* Image Modal - Full screen, auto-rotates 90deg on mobile */}
      <ImageModal $isOpen={isImageModalOpen} onClick={() => setIsImageModalOpen(false)}>
        <CloseButton onClick={(e) => { e.stopPropagation(); setIsImageModalOpen(false); }}>
          ×
        </CloseButton>
        <ModalImageContainer>
          {getImageInfo(currentAssignment.fenn).imageUrl && (
            <ModalImage
              src={getImageInfo(currentAssignment.fenn).imageUrl}
              alt={questionNumber}
            />
          )}
        </ModalImageContainer>
      </ImageModal>
    </AppContainer>
  );
};

export default YoxlamaPage;



